import {useEffect, useRef, useState} from 'react'
import {useDispatch} from 'react-redux'
import {
  getSurveyBannerDismissed,
  getSurveyStatementKey,
  setSurveyStatementKey
} from 'utils/persistence'

import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'

import {persistDismissal, restoreOrGetQuestion} from '../utils'

import type {SurveyBannerStatus} from '../constants'

const SUBMITTING_DURATION_MS = 2000

const readSurveyState = () => {
  const isVisible = !getSurveyBannerDismissed()

  return {
    isInitialized: true,
    isVisible,
    question: isVisible ? restoreOrGetQuestion() : null
  }
}

export const useTrustSurvey = () => {
  const dispatch = useDispatch()
  const isHydrated = useIsHydrated()
  // Client-only mounts can show the survey immediately; hydration must match the server.
  const [{isInitialized, isVisible, question}, setSurveyState] = useState(() =>
    isHydrated
      ? readSurveyState()
      : {isInitialized: false, isVisible: false, question: null}
  )
  const [status, setStatus] = useState<SurveyBannerStatus>('idle')
  const [selectedScore, setSelectedScore] = useState<number>()

  const {ref, inView} = useInView({threshold: 0.5, initialInView: false})
  const hasTrackedViewedRef = useRef(false)

  useEffect(() => {
    if (!isInitialized) setSurveyState(readSurveyState())
  }, [isInitialized])

  const questionId = question?.questionId

  useEffect(() => {
    if (questionId && getSurveyStatementKey() !== questionId) {
      setSurveyStatementKey(questionId)
    }
  }, [questionId])

  useEffect(() => {
    if (isVisible && inView && questionId && !hasTrackedViewedRef.current) {
      hasTrackedViewedRef.current = true
      dispatch(
        trackEvent({
          category: Category.User,
          entity: Entity.TrustSurveyBanner,
          action: Action.Viewed,
          payload: {questionId}
        })
      )
    }
  }, [isVisible, inView, questionId, dispatch])

  const onSelect = (score: number) => {
    if (status !== 'idle' || !question) {
      return
    }
    setSelectedScore(score)
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.TrustSurveyBannerResponse,
        action: Action.Submitted,
        payload: {questionId: question.questionId, responseRating: score}
      })
    )
    setStatus('submitting')
    setTimeout(() => {
      setStatus('submitted')
    }, SUBMITTING_DURATION_MS)
    persistDismissal()
  }

  const onClose = () => {
    persistDismissal()
    setSurveyState(state => ({...state, isVisible: false}))
  }

  return {
    isInitialized,
    isVisible,
    status,
    selectedScore,
    question,
    onSelect,
    onClose,
    ref
  }
}
